// Session-scoped keyed cache for the customer detail page (עמוד לקוח).
//
// SHARED FOUNDATION — owned by the page skeleton. Tab implementation agents
// CONSUME this module (never edit it). Modeled on the proven pattern in
// src/services/signatures.ts: module-level cache + inflight promise dedup +
// synchronous cached readers + stale-while-revalidate + fire-and-forget
// preload*() warmers — the trio that makes the send-signature dialog open with
// zero loading. Adds a subscribe/notify layer so React components re-render
// when a cache entry lands (useSessionCacheValue below).
//
// Typical tab usage:
//   const KEY = (contact: RealEstateContact) => `calls:${contact.id}`;
//   export function preloadCallsTab(contact: RealEstateContact): void {
//     void fetchCached(KEY(contact), () => loadCalls(contact)).catch(() => undefined);
//   }
//   // inside the tab component:
//   const { data, loading, error } = useSessionCacheValue(KEY(contact), () => loadCalls(contact));

import { useEffect, useState } from "react";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";

type CacheEntry = {
  value?: unknown;
  error?: Error;
  inflight?: Promise<unknown>;
  fetchedAt?: number;
  /** LRU bookkeeping — bumped on every read/write access to the entry. */
  touchedAt?: number;
  /** Bumped by setCached — lets in-flight fetches detect a newer imperative write. */
  writeEpoch?: number;
};

const cache = new Map<string, CacheEntry>();
const listeners = new Map<string, Set<() => void>>();

// Light LRU cap: hover-preloading hundreds of contacts must not accumulate
// unbounded per-contact data (full call lists, listings, sent-message and
// calendar arrays) in a long-lived tab. ~1500 keys ≈ ~150 contacts × ~10 keys
// each. Keys with a mounted subscriber or an in-flight fetch are never evicted.
const MAX_CACHE_ENTRIES = 1500;

const notify = (key: string): void => {
  listeners.get(key)?.forEach((cb) => {
    try {
      cb();
    } catch {
      // a broken subscriber must never take down the cache
    }
  });
};

const evictLeastRecentlyTouchedIfNeeded = (): void => {
  if (cache.size <= MAX_CACHE_ENTRIES) return;
  const evictable = Array.from(cache.entries())
    .filter(([key, entry]) => !listeners.has(key) && !entry.inflight)
    .sort((a, b) => (a[1].touchedAt ?? 0) - (b[1].touchedAt ?? 0));
  let excess = cache.size - MAX_CACHE_ENTRIES;
  for (const [key] of evictable) {
    if (excess <= 0) break;
    cache.delete(key);
    excess -= 1;
  }
};

const entryOf = (key: string): CacheEntry => {
  let entry = cache.get(key);
  if (!entry) {
    entry = {};
    cache.set(key, entry);
    evictLeastRecentlyTouchedIfNeeded();
  }
  entry.touchedAt = Date.now();
  return entry;
};

/** Synchronous read — undefined when the key was never (successfully) fetched. */
export function getCached<T>(key: string): T | undefined {
  const entry = cache.get(key);
  if (entry) entry.touchedAt = Date.now();
  return entry?.value as T | undefined;
}

/** Imperative write (e.g. optimistic updates after a mutation). Notifies subscribers. */
export function setCached<T>(key: string, value: T): void {
  const entry = entryOf(key);
  entry.value = value;
  entry.error = undefined;
  entry.fetchedAt = Date.now();
  entry.writeEpoch = (entry.writeEpoch ?? 0) + 1;
  notify(key);
}

/** Drops a key entirely (next fetchCached refetches). Notifies subscribers. */
export function invalidateCached(key: string): void {
  cache.delete(key);
  notify(key);
}

/**
 * Fetch with inflight dedup + stale-while-revalidate.
 *
 * - No cached value: runs `fetcher` (deduped), resolves with fresh data.
 * - Cached value younger than `maxAgeMs`: resolves immediately from cache.
 * - Cached value older than `maxAgeMs`: resolves immediately from cache AND
 *   kicks a background revalidation that notifies subscribers when done.
 */
export async function fetchCached<T>(
  key: string,
  fetcher: () => Promise<T>,
  { maxAgeMs = 30_000 }: { maxAgeMs?: number } = {}
): Promise<T> {
  const entry = entryOf(key);

  const startFetch = (): Promise<T> => {
    if (entry.inflight) return entry.inflight as Promise<T>;
    const epochAtStart = entry.writeEpoch ?? 0;
    const inflight = fetcher()
      .then((value) => {
        // A newer imperative setCached (optimistic update / live-listener
        // mirror) may have landed while this fetch was in flight — never
        // clobber it with the older read. Callers still get the fetched value.
        if ((entry.writeEpoch ?? 0) === epochAtStart) {
          entry.value = value;
          entry.error = undefined;
          entry.fetchedAt = Date.now();
        }
        return value;
      })
      .catch((err) => {
        entry.error = err instanceof Error ? err : new Error(String(err));
        throw entry.error;
      })
      .finally(() => {
        entry.inflight = undefined;
        notify(key);
      });
    entry.inflight = inflight;
    return inflight;
  };

  if (entry.value !== undefined) {
    const isStale = !entry.fetchedAt || Date.now() - entry.fetchedAt > maxAgeMs;
    // Serve stale immediately, revalidate silently in the background.
    if (isStale) void startFetch().catch(() => undefined);
    return entry.value as T;
  }

  return startFetch();
}

/** Subscribe to changes of one key. Returns the unsubscribe function. */
export function subscribeCached(key: string, callback: () => void): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(callback);
  return () => {
    set!.delete(callback);
    if (set!.size === 0) listeners.delete(key);
  };
}

/**
 * React binding: renders instantly from cache when warm (no flash), fetches +
 * subscribes otherwise. Safe on React 17 (plain useState + effect — no
 * useSyncExternalStore in this toolchain).
 */
export function useSessionCacheValue<T>(
  key: string,
  fetcher: () => Promise<T>,
  { maxAgeMs }: { maxAgeMs?: number } = {}
): { data: T | undefined; loading: boolean; error: Error | null; refresh: () => void } {
  const [, forceRender] = useState(0);

  const entry = cache.get(key);
  // Snapshot of what THIS render saw — lets the effect below detect a write
  // that settled in the commit→effect gap (its notify() had no subscriber yet).
  const renderedValue = entry?.value;
  const renderedError = entry?.error;
  const renderedInflight = entry?.inflight;

  useEffect(() => {
    const unsubscribe = subscribeCached(key, () => forceRender((n) => n + 1));
    // Close the missed-notify race: a preload resolving between the render
    // and this subscription notified nobody, and the synchronous fresh-value
    // path of fetchCached below won't notify either — so if the entry moved
    // since the render, force one re-render now.
    const current = cache.get(key);
    if (
      current?.value !== renderedValue ||
      current?.error !== renderedError ||
      current?.inflight !== renderedInflight
    ) {
      forceRender((n) => n + 1);
    }
    void fetchCached<T>(key, fetcher, { maxAgeMs }).catch(() => undefined);
    return unsubscribe;
    // `fetcher` is intentionally not a dependency — call sites pass inline
    // closures; the key is the identity (same convention as useLiveContact).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return {
    data: entry?.value as T | undefined,
    loading: !!entry?.inflight && entry?.value === undefined,
    error: entry?.error ?? null,
    refresh: () => {
      invalidateCached(key);
      void fetchCached<T>(key, fetcher, { maxAgeMs }).catch(() => undefined);
    }
  };
}

/**
 * Fire-and-forget warm-up of the WHOLE customer page for a contact: dynamically
 * imports the tab registry (warming the code chunk too — the page and its tabs
 * are lazy) and runs every tab's `preloadXxxTab(contact)`. Error-safe; safe to
 * call repeatedly (each tab's preload dedups through fetchCached).
 *
 * Called from the contacts table on row hover/pointerdown and from the page
 * itself on mount, so navigating to /dashboard/contacts/:contactId opens warm.
 */
export function preloadContactDetail(contact: RealEstateContact | null | undefined): void {
  if (!contact?.id) return;
  void import("../tabs/registry")
    .then(({ preloadAllContactTabs }) => preloadAllContactTabs(contact))
    .catch(() => undefined);
}

/**
 * Hover-intent gate for row-level preloads. A bare mouseenter would fire the
 * full per-contact preload fan-out once per row a pointer sweep crosses.
 * Instead: schedule `run` after `delayMs` of continuous hover, cancel on
 * leave, and fire immediately on pointerdown (real intent).
 *
 * Create ONE instance per row/cell (e.g. inside useMemo with a ref to the
 * latest contact) and spread its three handlers onto the hover target.
 */
export function createHoverIntentPreloader(
  run: () => void,
  delayMs = 150
): { onMouseEnter: () => void; onMouseLeave: () => void; onPointerDown: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const cancel = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return {
    onMouseEnter: (): void => {
      cancel();
      timer = setTimeout(() => {
        timer = null;
        run();
      }, delayMs);
    },
    onMouseLeave: cancel,
    onPointerDown: (): void => {
      cancel();
      run();
    }
  };
}
